import { Icon } from "@iconify/react";
import type { ReactNode } from "react";

export type NoticeAccent = "warning" | "gold";

/**
 * Full literal class strings per accent so Tailwind's content scanner detects
 * every utility. The public site runs under the `medieval` theme (not the
 * `dark` class), so `dark:` variants never apply here — these banners are
 * styled dark-native with low-opacity accent tints to match the rest of the
 * tower chrome (CooldownCounter, TeamStatusCard, ArchivedTournamentBanner)
 * instead of the old light `bg-*-50` boxes that rendered as cream blocks.
 */
const ACCENT: Record<
  NoticeAccent,
  { card: string; wash: string; chip: string; icon: string; title: string }
> = {
  warning: {
    card: "border-amber-500/25 bg-amber-500/5",
    wash: "from-amber-500/10 via-amber-500/5 to-transparent",
    chip: "border-amber-500/30 bg-amber-500/15",
    icon: "text-amber-400",
    title: "text-amber-300",
  },
  gold: {
    card: "border-[var(--border-gold)] bg-[var(--bg-card)]",
    wash: "from-[rgba(255,215,0,0.1)] via-transparent to-transparent",
    chip: "border-[var(--border-gold)] bg-[var(--bg-deep)]",
    icon: "text-[var(--gold)]",
    title: "text-[var(--gold)]",
  },
};

interface NoticeBannerProps {
  /** Iconify icon name rendered in the leading chip. */
  icon: string;
  /** Already-translated heading. */
  title: string;
  /** Already-translated body text (optional). */
  description?: ReactNode;
  /** Accent theme. Defaults to the amber warning style. */
  accent?: NoticeAccent;
  /** Extra content below the description, e.g. a live countdown. */
  children?: ReactNode;
  /** Extra classes on the root (e.g. spacing like `mb-6`). */
  className?: string;
}

/**
 * Shared notice/callout banner for the tower pages: an accented icon chip, a
 * Cinzel heading and supporting copy on a dark heraldic card. Keeps the
 * create/find banners visually consistent with the rest of the dark layout.
 */
export function NoticeBanner({
  icon,
  title,
  description,
  accent = "warning",
  children,
  className = "",
}: NoticeBannerProps) {
  const a = ACCENT[accent];
  return (
    <div
      className={`relative flex items-start gap-3.5 overflow-hidden rounded-xl border p-4 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] ${a.card} ${className}`}
    >
      {/* Subtle accent wash so the card glows without losing contrast. */}
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${a.wash}`}
      />
      {/* Crest-style icon chip echoing the tower badges elsewhere. */}
      <span
        aria-hidden
        className={`relative flex size-10 shrink-0 items-center justify-center rounded-lg border ${a.chip}`}
      >
        <Icon icon={icon} className={`size-5 ${a.icon}`} />
      </span>
      <div className="relative min-w-0 flex-1">
        <p
          className={`font-cinzel text-sm font-semibold uppercase tracking-wide ${a.title}`}
        >
          {title}
        </p>
        {description ? (
          <p className="mt-0.5 text-sm leading-snug text-[var(--text-secondary)]">
            {description}
          </p>
        ) : null}
        {children}
      </div>
    </div>
  );
}
